import * as React from 'react';
import {
  isMfaChallenge,
  refreshSession,
  signIn,
  signOut,
  UNAVAILABLE,
  type MfaChallenge,
  type Session,
} from './accounts.ts';
import { api, getAccessToken, setAccessToken } from '../platform/index.ts';
import { onOutage, reconnectDelay, sessionVerdict, type SessionVerdict } from '../platform/index.ts';
import { queryClient } from '../platform/index.ts';

export interface Me {
  subject: string;
  email: string;
  roles: string[];
}

/** GET /api/capabilities: what the caller may do, per organization and platform-wide. */
export type Capabilities = NonNullable<Awaited<ReturnType<typeof loadCapabilities>>>;

export type AuthState =
  | { status: 'loading' }
  /** No caller known yet and the platform cannot answer (it restarts): retried, never "signed out". */
  | { status: 'reconnecting' }
  | { status: 'signed-in'; user: Me; capabilities: Capabilities | null }
  | { status: 'signed-out'; error?: string };

/** The api does not answer (a restart): when the next try runs. Null while it answers. */
export type Reconnect = { attempt: number; retryAt: number } | null;

type AuthContextValue = {
  state: AuthState;
  /** Set while the platform does not answer; the shell shows "Reconnecting…" (Board #2649). */
  reconnect: Reconnect;
  /** Tries the platform again now, instead of at `reconnect.retryAt`. */
  retryNow: () => void;
  refresh: () => Promise<void>;
  /** A session, or the second factor the sign-in still needs (ADR 0014 §9). */
  login: (email: string, password: string) => Promise<MfaChallenge | null>;
  /** Adopts a session the two-factor step started. */
  signedIn: (s: Session) => Promise<void>;
  logout: () => void;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

async function loadCapabilities() {
  try {
    const { data } = await api.GET('/api/capabilities');
    // Anything but a well-formed answer means "no capabilities known": the shell hides admin items.
    return data && Array.isArray(data.orgs) && Array.isArray(data.platform) ? data : null;
  } catch {
    return null;
  }
}

interface CallerAnswer {
  verdict: SessionVerdict;
  /** The api's status; null when it did not answer. */
  status: number | null;
  user?: Me;
  capabilities: Capabilities | null;
}

/** GET /api/me and the caller's capabilities, in parallel: one round trip before the shell, not two. */
async function askCaller(): Promise<CallerAnswer> {
  try {
    // Never from the read cache: this is the check that the api answers now.
    const [{ data, response }, capabilities] = await Promise.all([
      api.GET('/api/me', { cache: 'no-store' }),
      loadCapabilities(),
    ]);
    const verdict = sessionVerdict(response.status);
    // A 2xx without a caller is not an answer to rely on: try again.
    if (verdict === 'signed-in' && !data)
      return { verdict: 'unavailable', status: response.status, capabilities };
    return { verdict, status: response.status, ...(data ? { user: data } : {}), capabilities };
  } catch {
    return { verdict: 'unavailable', status: null, capabilities: null };
  }
}

/**
 * Resolves the caller: a session from the accounts refresh cookie (if any), then GET /api/me and the
 * caller's capabilities. Only an auth answer (401) means signed out: a platform that does not answer
 * (no answer, 502/503/504, any 5xx — the api restarting) is retried with a backoff while the shell shows
 * "Reconnecting…", the person stays where they are (Board #2649). The access token is refreshed shortly
 * before it expires.
 */
export function AuthProvider({ children }: { children?: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>({ status: 'loading' });
  const [reconnect, setReconnect] = React.useState<Reconnect>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const attempt = React.useRef(0);
  const reconnecting = React.useRef(false);
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const establishRef = React.useRef<(renew: boolean) => Promise<void>>(async () => {});

  const adopt = React.useCallback((s: Session | null) => {
    clearTimeout(timer.current);
    setAccessToken(s?.accessToken);
    if (!s) return;
    // Refresh at 80% of the token's life. An accounts service that cannot answer is asked again shortly
    // (the token is still good); a failed refresh signs out on the next API 401.
    const renewIn = (ms: number) => {
      timer.current = setTimeout(() => {
        void refreshSession().then((next) => {
          if (next === UNAVAILABLE) renewIn(5000);
          else if (next) adopt(next);
        });
      }, ms);
    };
    renewIn(Math.max(5, s.expiresIn * 0.8) * 1000);
  }, []);

  /** The platform does not answer: try again after the backoff; a signed-in person keeps their page. */
  const scheduleReconnect = React.useCallback(() => {
    clearTimeout(retryTimer.current);
    reconnecting.current = true;
    const n = attempt.current++;
    const delay = reconnectDelay(n);
    setReconnect({ attempt: n, retryAt: Date.now() + delay });
    setState((s) => (s.status === 'signed-in' ? s : { status: 'reconnecting' }));
    retryTimer.current = setTimeout(() => void establishRef.current(false), delay);
  }, []);

  /** The platform answered: the reconnect is over, and the pages' cached data reloads. */
  const online = React.useCallback(() => {
    clearTimeout(retryTimer.current);
    attempt.current = 0;
    if (reconnecting.current) {
      reconnecting.current = false;
      setReconnect(null);
      void queryClient.invalidateQueries();
    }
  }, []);

  /**
   * Who is the caller? `renew` (or no access token yet) first asks the accounts refresh; with a token, a
   * 401 (it may have expired while the api was down) gets one refresh before it counts as signed out.
   */
  const establish = React.useCallback(
    async (renew: boolean) => {
      let renewed = false;
      const renewSession = async () => {
        renewed = true;
        const s = await refreshSession();
        if (s === UNAVAILABLE) return false;
        adopt(s);
        return true;
      };
      if ((renew || !getAccessToken()) && !(await renewSession())) return scheduleReconnect();
      let answer = await askCaller();
      if (answer.status === 401 && !renewed) {
        if (!(await renewSession())) return scheduleReconnect();
        answer = await askCaller();
      }
      if (answer.verdict === 'unavailable') return scheduleReconnect();
      online();
      if (answer.verdict === 'signed-in' && answer.user) {
        const next: AuthState = { status: 'signed-in', user: answer.user, capabilities: answer.capabilities };
        // The same caller as before (a probe, a reconnect) keeps the same state object: nothing re-renders.
        setState((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      } else
        setState({
          status: 'signed-out',
          ...(answer.status === 401 ? {} : { error: `The API answered ${answer.status}.` }),
        });
    },
    [adopt, scheduleReconnect, online],
  );
  establishRef.current = establish;

  const refresh = React.useCallback(() => establish(true), [establish]);

  React.useEffect(() => {
    void refresh();
    return () => {
      clearTimeout(timer.current);
      clearTimeout(retryTimer.current);
    };
  }, [refresh]);

  // A request of a page got no answer or a 502/503/504. That may be the api restarting, or one route's own
  // 503 (a module whose dependency is down): the session check decides. It runs at once (one at a time, at
  // most every 5 s while the api answers); only when it cannot answer either does the shell show
  // "Reconnecting…", and the signed-in person stays on their page. Reports during a reconnect (its own
  // probes) change nothing.
  const probing = React.useRef(false);
  const lastProbe = React.useRef(Number.NEGATIVE_INFINITY);
  const probeTimer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  React.useEffect(() => {
    const probe = () => {
      probeTimer.current = undefined;
      if (stateRef.current.status !== 'signed-in' || reconnecting.current || probing.current) return;
      probing.current = true;
      lastProbe.current = Date.now();
      void establishRef.current(false).finally(() => {
        probing.current = false;
      });
    };
    const off = onOutage(() => {
      if (stateRef.current.status !== 'signed-in' || reconnecting.current || probing.current) return;
      if (probeTimer.current !== undefined) return;
      // A report soon after the last check is checked when the 5 s are up, never dropped.
      probeTimer.current = setTimeout(probe, Math.max(0, lastProbe.current + 5000 - Date.now()));
    });
    return () => {
      off();
      clearTimeout(probeTimer.current);
    };
  }, []);

  const retryNow = React.useCallback(() => {
    clearTimeout(retryTimer.current);
    void establish(false);
  }, [establish]);

  const login = React.useCallback(
    async (email: string, password: string) => {
      const r = await signIn(email, password);
      if (isMfaChallenge(r)) return r;
      adopt(r);
      await establish(false);
      return null;
    },
    [adopt, establish],
  );

  const signedIn = React.useCallback(
    async (s: Session) => {
      adopt(s);
      await establish(false);
    },
    [adopt, establish],
  );

  const logout = React.useCallback(() => {
    adopt(null);
    clearTimeout(retryTimer.current);
    reconnecting.current = false;
    setReconnect(null);
    void signOut();
    setState({ status: 'signed-out' });
  }, [adopt]);

  const value = React.useMemo(
    () => ({ state, reconnect, retryNow, refresh, login, signedIn, logout }),
    [state, reconnect, retryNow, refresh, login, signedIn, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/**
 * The roles the shell's presentation gates read: the token's roles plus the caller's platform and
 * organization roles from GET /api/capabilities (the server stays the enforcement point).
 */
export function shellRoles(state: AuthState): string[] {
  if (state.status !== 'signed-in') return [];
  const caps = state.capabilities;
  return [
    ...new Set([
      ...state.user.roles,
      ...(caps?.platformAdmin ? ['platform_admin'] : []),
      ...(caps?.orgs.map((o) => o.role) ?? []),
    ]),
  ];
}

/**
 * Whether the signed-in person is an APP USER only: no platform admin, and no role in any organization above App User
 * (an App User sees only the apps shared with them, by their share links; nothing of cortex itself — the owner,
 * 2026-11-03). Unknown capabilities (a failed read) are not taken for it: the server stays the enforcement point.
 */
export function isAppUserOnly(state: AuthState): boolean {
  if (state.status !== 'signed-in' || !state.capabilities) return false;
  const caps = state.capabilities;
  if (caps.platformAdmin || state.user.roles.includes('platform_admin')) return false;
  return caps.orgs.every((o) => o.role === 'app_user');
}

/** Display name and initials from the email's local part ("ada.lovelace@x" → "Ada Lovelace", "AL"). */
export function displayName(user: Pick<Me, 'email'> | null | undefined) {
  const local = (user?.email ?? '').split('@')[0] ?? '';
  const words = local.split(/[._-]+/).filter(Boolean);
  const fullName = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'User';
  const initials =
    words
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('') || 'U';
  return { fullName, initials };
}
